// src/components/Leave/LeaveBalance.jsx
import React, { useState, useEffect } from 'react';
import { FiRefreshCw, FiCalendar } from 'react-icons/fi';
import { useAppContext } from '../../context/AppContext';

const LeaveBalance = () => {
  const { leave } = useAppContext();
  const { getMyBalance, loading } = leave;

  const [balance, setBalance] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadBalance = async (selectedYear) => {
    setError(null);
    setIsLoading(true);
    try {
      const data = await getMyBalance(selectedYear);
      setBalance(data);
    } catch (err) {
      setError(err.message || 'Failed to load balance');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBalance(year);
  }, [year]);

  const handleYearChange = (e) => {
    setYear(parseInt(e.target.value));
  };

  // ✅ Check both loading states
  if ((loading || isLoading) && !balance) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
        <button
          onClick={() => loadBalance(year)}
          className="ml-3 text-sm font-medium hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!balance) {
    return (
      <div className="text-center py-8 text-gray-500">
        No balance data available
      </div>
    );
  }

  const summary = balance.summary || {};

  return (
    <div className="space-y-6">
      {/* Header with Year Selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-gray-800">Leave Balance</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <FiCalendar className="text-gray-400" />
            <select
              value={year}
              onChange={handleYearChange}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {[2023, 2024, 2025, 2026, 2027].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => loadBalance(year)}
            disabled={isLoading}
            className="p-2 text-gray-500 hover:text-indigo-600 transition rounded-lg hover:bg-indigo-50"
          >
            <FiRefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Casual Leave */}
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Casual Leave</p>
              <p className="text-2xl font-bold text-gray-800">
                {summary.casual?.remaining || 0}
                <span className="text-sm font-normal text-gray-400 ml-1">
                  / {summary.casual?.total || 0}
                </span>
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                style={{
                  width: `${summary.casual?.total > 0 ? (summary.casual?.used / summary.casual?.total) * 100 : 0}%`
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Used: {summary.casual?.used || 0}</span>
              <span>{summary.casual?.percentage || 0}% used</span>
            </div>
          </div>
        </div>

        {/* Sick Leave */}
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Sick Leave</p>
              <p className="text-2xl font-bold text-gray-800">
                {summary.sick?.remaining || 0}
                <span className="text-sm font-normal text-gray-400 ml-1">
                  / {summary.sick?.total || 0}
                </span>
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-red-600 h-2 rounded-full transition-all duration-500"
                style={{
                  width: `${summary.sick?.total > 0 ? (summary.sick?.used / summary.sick?.total) * 100 : 0}%`
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Used: {summary.sick?.used || 0}</span>
              <span>{summary.sick?.percentage || 0}% used</span>
            </div>
          </div>
        </div>

        {/* Unpaid Days */}
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Unpaid Leave</p>
              <p className="text-2xl font-bold text-gray-800">
                {summary.unpaid?.days || 0}
                <span className="text-sm font-normal text-gray-400 ml-1">days</span>
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v1m0 1v1m0 1v1" />
              </svg>
            </div>
          </div>
          <div className="mt-3">
            <p className="text-xs text-gray-500">
              Total unpaid leave taken this year
            </p>
          </div>
        </div>
      </div>

      {/* Total Summary */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500">Total Available</p>
            <p className="text-lg font-bold text-gray-800">
              {summary.total?.available || 0}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Used</p>
            <p className="text-lg font-bold text-gray-800">
              {summary.total?.used || 0}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Entitled</p>
            <p className="text-lg font-bold text-gray-800">
              {summary.total?.total || 0}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Utilization</p>
            <p className="text-lg font-bold text-gray-800">
              {summary.total?.total > 0
                ? Math.round((summary.total?.used / summary.total?.total) * 100)
                : 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveBalance;